import { useCallback } from 'react';
import { z } from 'zod';
import { useLocalStorage } from '../../../lib/hooks/use-local-storage.ts';

const NAV_GROUP_COLLAPSE_STORAGE_KEY = 'plato-nav-collapsed-groups';

const collapsedGroupsSchema = z.record(z.string(), z.boolean());

type CollapsedGroups = z.infer<typeof collapsedGroupsSchema>;

/**
 * Per-group collapsed/expanded state for the manager menu section headers,
 * persisted so a user's collapsed sections survive reloads. Groups default
 * to expanded (absent key === expanded). The rep menu has no labelled
 * groups, so it never reads this.
 */
export const useNavGroupCollapse = (): {
    isCollapsed: (id: string) => boolean;
    toggle: (id: string) => void;
} => {
    const [collapsed, setCollapsed] = useLocalStorage<CollapsedGroups>(
        NAV_GROUP_COLLAPSE_STORAGE_KEY,
        {},
        { schema: collapsedGroupsSchema },
    );

    const isCollapsed = useCallback((id: string): boolean => collapsed[id] ?? false, [collapsed]);

    const toggle = useCallback(
        (id: string): void => {
            setCollapsed((prev) => ({ ...prev, [id]: !(prev[id] ?? false) }));
        },
        [setCollapsed],
    );

    return { isCollapsed, toggle };
};
